import React, { useState, useEffect, useRef } from 'react';
import {
  Upload,
  Sparkles,
  Copy,
  Plus,
  Globe,
  Facebook,
  Linkedin,
  Instagram,
  Twitter,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '../../../app/providers/AuthProvider';
import { schoolsApi, settingsApi } from '../../../lib/api';

type TabType = 'general' | 'vision' | 'links';

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const schoolTypes = [
  { value: 'online', label: 'Online Only' },
  { value: 'k12', label: 'K-12' },
  { value: 'college', label: 'College' },
  { value: 'university', label: 'University' },
];

const socialPlatforms = [
  { id: 'website', label: 'School Website', icon: Globe, placeholder: 'https://yourschool.edu' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/yourschool' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/company/yourschool' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/yourschool' },
  { id: 'twitter', label: 'Twitter', icon: Twitter, placeholder: 'https://twitter.com/yourschool' },
];

interface SchoolData {
  name: string;
  phone: string;
  email: string;
  schoolType: string;
  primaryColor: string;
  secondaryColor: string;
  country: string;
  city: string;
  postalCode: string;
  addressLine1: string;
  activeDays: string[];
  startTime: string;
  endTime: string;
  semester1Start: string;
  semester1End: string;
  semester2Start: string;
  semester2End: string;
  codeOfConduct: string;
  schoolVision: string;
  socialLinks: Record<string, string>;
  website: string;
}

export function SchoolInformationSection() {
  const { currentSchool } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Form data
  const [formData, setFormData] = useState<SchoolData>({
    name: '',
    phone: '',
    email: '',
    schoolType: 'k12',
    primaryColor: '#14b8a6',
    secondaryColor: '#0d9488',
    country: 'Israel',
    city: '',
    postalCode: '',
    addressLine1: '',
    activeDays: ['Mon', 'Tue', 'Wed', 'Thu'],
    startTime: '08:00',
    endTime: '17:00',
    semester1Start: '2024-09-01',
    semester1End: '2025-01-31',
    semester2Start: '2025-02-01',
    semester2End: '2025-06-30',
    codeOfConduct: '',
    schoolVision: '',
    socialLinks: {
      website: '',
      facebook: '',
      linkedin: '',
      instagram: '',
      twitter: '',
    },
    website: '',
  });

  // Load school data
  useEffect(() => {
    const fetchSchoolData = async () => {
      if (!currentSchool?.id) {
        setLoading(false);
        return;
      }

      try {
        const response = await schoolsApi.get(currentSchool.id);
        const school = response.school;

        setFormData((prev) => ({
          ...prev,
          name: school.name || '',
          phone: school.phone || '',
          email: school.email || '',
          schoolType: school.school_type || 'k12',
          primaryColor: school.primary_color || '#14b8a6',
          secondaryColor: school.secondary_color || '#0d9488',
          country: school.country || 'Israel',
          city: school.city || '',
          postalCode: school.postal_code || '',
          addressLine1: school.address_line1 || '',
          website: school.website || '',
          codeOfConduct: school.code_of_conduct || '',
          schoolVision: school.vision || '',
          socialLinks: {
            website: school.website || '',
            facebook: school.social_facebook || '',
            linkedin: school.social_linkedin || '',
            instagram: school.social_instagram || '',
            twitter: school.social_twitter || '',
          },
        }));
        if (school.logo_url) {
          setLogoUrl(school.logo_url);
        }
      } catch (err) {
        console.error('Failed to load school data:', err);
        setError('Failed to load school settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSchoolData();
  }, [currentSchool?.id]);

  const updateField = <K extends keyof SchoolData>(field: K, value: SchoolData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSuccess(false);
    setError(null);
  };

  const toggleDay = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      activeDays: prev.activeDays.includes(day)
        ? prev.activeDays.filter((d) => d !== day)
        : [...prev.activeDays, day],
    }));
    setSuccess(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    setError(null);

    try {
      const result = await settingsApi.uploadBranding('logo', file);
      if (result.url) {
        setLogoUrl(result.url);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else if (result.error) {
        setError(result.error);
      }
    } catch (err: any) {
      console.error('Logo upload failed:', err);
      setError(err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      // Reset input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    if (!currentSchool?.id) {
      setError('No school selected');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await schoolsApi.update(currentSchool.id, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        school_type: formData.schoolType,
        primary_color: formData.primaryColor,
        secondary_color: formData.secondaryColor,
        country: formData.country,
        city: formData.city,
        postal_code: formData.postalCode,
        address_line1: formData.addressLine1,
        website: formData.socialLinks.website || formData.website,
        code_of_conduct: formData.codeOfConduct,
        vision: formData.schoolVision,
        social_facebook: formData.socialLinks.facebook,
        social_linkedin: formData.socialLinks.linkedin,
        social_instagram: formData.socialLinks.instagram,
        social_twitter: formData.socialLinks.twitter,
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Failed to save school settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general' as const, label: 'General Details' },
    { id: 'vision' as const, label: 'Vision & Code' },
    { id: 'links' as const, label: 'School Links' },
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Status Messages */}
      {success && (
        <div className="mx-6 mt-6 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <Check className="w-5 h-5" />
          Settings saved successfully!
        </div>
      )}
      {error && (
        <div className="mx-6 mt-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 px-6">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {activeTab === 'general' && (
          <div className="space-y-6">
            {/* School Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School Logo</label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden">
                  {logoUrl ? (
                    <img src={logoUrl} alt="School Logo" className="w-full h-full object-cover" />
                  ) : (
                    <Upload className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="px-4 py-2 text-sm font-medium text-teal-600 border border-teal-600 rounded-lg hover:bg-teal-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {uploadingLogo && <Loader2 className="w-4 h-4 animate-spin" />}
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </button>
              </div>
            </div>

            {/* School Name & Phone */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {/* School Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">School Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* School Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School Type</label>
              <div className="flex gap-2">
                {schoolTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => updateField('schoolType', type.value)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      formData.schoolType === type.value
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* School Colors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School Colors</label>
              <div className="flex gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Primary</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => updateField('primaryColor', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.primaryColor}
                      onChange={(e) => updateField('primaryColor', e.target.value)}
                      className="w-24 px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Secondary</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.secondaryColor}
                      onChange={(e) => updateField('secondaryColor', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.secondaryColor}
                      onChange={(e) => updateField('secondaryColor', e.target.value)}
                      className="w-24 px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={formData.country}
                  onChange={(e) => updateField('country', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="Israel">Israel</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zipcode</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => updateField('postalCode', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {/* Semester Dates */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Semester Dates</label>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600 w-24">1st Semester:</span>
                  <input
                    type="date"
                    value={formData.semester1Start}
                    onChange={(e) => updateField('semester1Start', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="date"
                    value={formData.semester1End}
                    onChange={(e) => updateField('semester1End', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600 w-24">2nd Semester:</span>
                  <input
                    type="date"
                    value={formData.semester2Start}
                    onChange={(e) => updateField('semester2Start', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="date"
                    value={formData.semester2End}
                    onChange={(e) => updateField('semester2End', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <button className="text-sm text-teal-600 hover:text-teal-700">
                  + Add 3rd Semester
                </button>
              </div>
            </div>

            {/* Active Days */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Active Days</label>
              <div className="flex gap-2">
                {daysOfWeek.map((day) => (
                  <button
                    key={day}
                    onClick={() => toggleDay(day)}
                    className={`w-12 h-10 text-sm font-medium rounded-lg border transition-colors ${
                      formData.activeDays.includes(day)
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            {/* Active Hours */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Active Hours</label>
              <div className="flex items-center gap-4">
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => updateField('startTime', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <span className="text-gray-400">to</span>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => updateField('endTime', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <button className="mt-2 text-sm text-teal-600 hover:text-teal-700">
                Custom Hours & Days
              </button>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        )}

        {activeTab === 'vision' && (
          <div className="space-y-6">
            {/* Code of Conduct */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">School Code of Conduct</label>
                <button className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700">
                  <Sparkles className="w-4 h-4" />
                  Let Us Write it For You
                </button>
              </div>
              <textarea
                value={formData.codeOfConduct}
                onChange={(e) => updateField('codeOfConduct', e.target.value)}
                placeholder="Enter your school's code of conduct..."
                rows={6}
                maxLength={1000}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              />
              <div className="flex justify-between mt-1">
                <button className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1">
                  <Upload className="w-4 h-4" />
                  Upload PDF/Word
                </button>
                <span className="text-xs text-gray-400">{formData.codeOfConduct.length}/1000</span>
              </div>
            </div>

            {/* School Vision */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">School Vision for Education</label>
                <button className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700">
                  <Sparkles className="w-4 h-4" />
                  Let Us Write it For You
                </button>
              </div>
              <textarea
                value={formData.schoolVision}
                onChange={(e) => updateField('schoolVision', e.target.value)}
                placeholder="Enter your school's vision statement..."
                rows={6}
                maxLength={1000}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              />
              <div className="flex justify-between mt-1">
                <button className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1">
                  <Upload className="w-4 h-4" />
                  Upload PDF/Word
                </button>
                <span className="text-xs text-gray-400">{formData.schoolVision.length}/1000</span>
              </div>
            </div>

            {/* Add New Field */}
            <button className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700">
              <Plus className="w-4 h-4" />
              Add New Field
            </button>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        )}

        {activeTab === 'links' && (
          <div className="space-y-4">
            {socialPlatforms.map((platform) => (
              <div key={platform.id} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <platform.icon className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{platform.label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={formData.socialLinks[platform.id] || ''}
                      onChange={(e) =>
                        updateField('socialLinks', {
                          ...formData.socialLinks,
                          [platform.id]: e.target.value,
                        })
                      }
                      placeholder={platform.placeholder}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    <button
                      onClick={() => copyToClipboard(formData.socialLinks[platform.id] || '')}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Add New Link */}
            <button className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 mt-4">
              <Plus className="w-4 h-4" />
              Add New Link
            </button>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
